<template>
  <div class="width-100">
    <h2 class="section-title">{{ $t('analysesSearch.title') }}</h2>

    <div class="form-block analyses-search-form">
      <ValidationObserver
        ref="analysesSearchForm"
        v-slot="{ handleSubmit, invalid }"
      >
        <el-form
          class="operations-search-form"
          size="small"
          label-position="top"
        >
          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="18">
              <el-form-item>
                <span slot="label">{{
                  $t('analysesSearch.search.customerName')
                }}</span>
                <el-autocomplete
                  class="width-100"
                  v-model="formData.customerName"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchCustomer({ text, cb, field: 'name', exact: false });
                    }
                  "
                  :trigger-on-focus="false"
                  @select="selectCustomer"
                  @change="emptySelectedCustomer"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </el-form-item>
            </el-col>

            <el-col :span="6">
              <el-form-item>
                <span slot="label">{{ $t('analysesSearch.search.code') }}</span>
                <el-autocomplete
                  class="width-100"
                  v-model="formData.code"
                  value-key="label"
                  clearable
                  :fetch-suggestions="
                    (text, cb) => {
                      searchCustomer({ text, cb, field: 'id', exact: true });
                    }
                  "
                  :trigger-on-focus="false"
                  @select="selectCustomer"
                  @change="emptySelectedCustomer"
                >
                  <template slot-scope="{ item }">
                    <AutocompleteItem :item="item" />
                  </template>
                </el-autocomplete>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="6">
              <FormItem :label="$t('analysesSearch.sampleReceivedFrom')">
                <el-date-picker
                  v-model="formData.sampleReceivedFrom"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>

            <el-col :span="6">
              <FormItem :label="$t('analysesSearch.sampleReceivedTo')">
                <el-date-picker
                  v-model="formData.sampleReceivedTo"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>

            <el-col :span="6">
              <FormItem :label="$t('analysesSearch.sampleCompletedFrom')">
                <el-date-picker
                  v-model="formData.sampleCompletedFrom"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>

            <el-col :span="6">
              <FormItem :label="$t('analysesSearch.sampleCompletedTo')">
                <el-date-picker
                  v-model="formData.sampleCompletedTo"
                  @change="updateStoredForm"
                  format="dd-MM-yyyy"
                  value-format="yyyy-MM-dd"
                  type="date"
                  placeholder=""
                />
              </FormItem>
            </el-col>
          </el-row>
          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="4">
              <FormItem :label="$t('analysesSearch.lab')">
                <el-select
                  v-model="formData.commodityCode"
                  placeholder=""
                  @change="updateStoredForm"
                  clearable
                >
                  <el-option
                    v-for="laboratory in laboratories"
                    :key="laboratory"
                    :value="laboratory"
                    :label="$t(`constants.${laboratory}`)"
                  />
                </el-select>
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('analysesSearch.sector')">
                <el-select
                  v-model="formData.sector"
                  placeholder=""
                  @change="updateStoredForm"
                  clearable
                >
                  <el-option
                    v-for="option in sectorOptions"
                    :key="option.value"
                    :label="$t(option.label)"
                    :value="option.value"
                  />
                </el-select>
              </FormItem>
            </el-col>

            <el-col :span="8">
              <FormItem :label="$t('analysesSearch.sampleDescription')">
                <el-select
                  v-model="formData.sampleDescription"
                  filterable
                  placeholder=""
                  @change="updateStoredForm"
                  clearable
                >
                  <el-option
                    v-for="option in sampleDescriptionOptions"
                    :key="option.value"
                    :label="$t(option.label)"
                    :value="option.value"
                  />
                </el-select>
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('analysesSearch.analysisCode')">
                <el-input
                  v-model="formData.analysisCode"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('analysesSearch.method')">
                <el-input
                  v-model="formData.method"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>
          </el-row>
          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="4">
              <FormItem :label="$t('analysesSearch.symbol')">
                <AutocompleteSymbol
                  v-model="symbolBuffer"
                  @change="addedSymbol"
                />
              </FormItem>
              <FilterTags
                :filter-data="symbolBufferArray"
                :filter="{ name: 'symbolBuffer' }"
                @tagClosed="removedSymbol"
              />
            </el-col>
            <el-col :span="4">
              <FormItem :label="$t('analysesSearch.symbollot')">
                <el-input
                  v-model="symbolLotBuffer"
                  @change="addedSymbolLot"
                />
              </FormItem>
              <FilterTags
                :filter-data="symbolLotBufferArray"
                :filter="{ name: 'symbolLotBuffer' }"
                @tagClosed="removedSymbolLot"
              />
            </el-col>
          </el-row>

          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="4">
              <FormItem :label="$t('analysesSearch.lot')">
                <el-input
                  v-model="formData.lot"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('analysesSearch.labSampleCode')">
                <el-input
                  v-model="formData.labSampleCode"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('analysesSearch.sampleName')">
                <el-input
                  v-model="formData.productName"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('analysesSearch.status')">
                <el-select
                  v-model="formData.sampleStatus"
                  placeholder=""
                  @change="updateStoredForm"
                  clearable
                >
                  <el-option
                    v-for="status in Object.keys(LAB_SAMPLE_STATUSES_DESC)"
                    :key="status"
                    :label="
                      $t(
                        `enums.sampleStatuses.${LAB_SAMPLE_STATUSES_DESC[status]}`
                      )
                    "
                    :value="status"
                  />
                </el-select>
              </FormItem>
            </el-col>

            <el-col :span="4">
              <FormItem :label="$t('analysesSearch.result')">
                <el-input
                  v-model="formData.value"
                  @change="updateStoredForm"
                />
              </FormItem>
            </el-col>
          </el-row>

          <el-row
            :gutter="20"
            type="flex"
          >
            <el-col :span="8">
              <FormItem
                rules="required"
                :label="$t('analysesSearch.printType')"
              >
                <el-radio-group
                  v-model="formData.printType"
                  @change="updateStoredForm"
                >
                  <el-radio
                    class="work-planning-form__radio"
                    v-for="type in printTypes"
                    :key="type.value"
                    :label="type.value"
                    >{{ type.label }}</el-radio
                  >
                </el-radio-group>
              </FormItem>
            </el-col>
          </el-row>

          <FormButtons>
            <el-button
              type="primary"
              native-type="submit"
              :disabled="invalid"
              @click.prevent="countSearchResults"
            >
              {{ $t('analysesSearch.searchSamples') }}
            </el-button>
            <span class="analyses-search-form analyses-search-form__results">
              {{ searchMessage }}
            </span>
            <span
              v-if="isLimitReached"
              class="analyses-search-form analyses-search-form__results"
            >
              {{
                $t('analysesSearch.limitReached', {
                  count: MAX_PRINTABLE_RESULTS,
                })
              }}
            </span>
          </FormButtons>

          <FormButtons>
            <el-button
              type="primary"
              native-type="submit"
              :disabled="invalid || !searchDone || isLimitReached"
              @click.prevent="handleSubmit(exportResultsExcel)"
            >
              {{ $t('analysesSearch.excelExport') }}
            </el-button>

            <el-button
              type="primary"
              native-type="submit"
              :disabled="invalid || !searchDone || isLimitReached"
              @click.prevent="handleSubmit(printSearchResults)"
            >
              {{ $t('analysesSearch.print') }}
            </el-button>
          </FormButtons>
        </el-form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import printJS from 'print-js';
  import fileSaver from 'file-saver';

  import { customersApi, samplesApi } from '@/api';
  import {
    laboratories,
    sectorsList,
    sampleDescriptionsList,
    LAB_SAMPLE_STATUSES_DESC,
  } from '@/config/constants';
  import FormItem from '@/components/FormItem.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';
  import AutocompleteSymbol from '@/components/AutocompleteSymbol.vue';
  import FilterTags from '@/components/FilterTags.vue';
  import { enumOptions } from '@/config/enumOptions';

  const MAX_PRINTABLE_RESULTS = 200;

  const initialFormData = {
    customerName: '',
    code: '',
    customerId: null,
    sampleReceivedFrom: null, // analysisStart
    sampleReceivedTo: null, // analysisStart
    sampleCompletedFrom: null, // analysisEnd
    sampleCompletedTo: null, // analysisEnd
    commodityCode: null,
    symbolArray: [],
    symbolLotArray: [],
    sector: null,
    sampleDescription: null,
    analysisCode: null,
    method: null,
    lot: null,
    labSampleCode: null,
    productName: null,
    sampleStatus: null,
    value: null,
    printType: null,
  };

  export default {
    name: 'AnalysesSearch',
    components: {
      FormItem,
      FormButtons,
      AutocompleteItem,
      AutocompleteSymbol,
      FilterTags,
    },
    data() {
      return {
        formData: { ...initialFormData },
        symbolBuffer: '',
        symbolLotBuffer: '',
        printTypes: [
          { label: this.$t('analysesSearch.brief'), value: 'brief' },
          { label: this.$t('analysesSearch.detailed'), value: 'detailed' },
        ],
        searchDone: false,
        searchCount: 0,
        searchMessage: null,
        laboratories,
        sectorsList,
        sampleDescriptionsList,
        LAB_SAMPLE_STATUSES_DESC,
        MAX_PRINTABLE_RESULTS,
      };
    },
    computed: {
      sectorOptions() {
        return enumOptions(
          sectorsList,
          'samples.sectors',
          this.formData.sector
        );
      },
      sampleDescriptionOptions() {
        return enumOptions(
          sampleDescriptionsList,
          'samples.sampleDescriptions',
          this.formData.sampleDescription
        );
      },
      ...mapGetters(['forms']),
      symbolBufferArray() {
        return this.formData.symbolArray.map((curr) => ({
          key: curr,
          label: curr,
        }));
      },
      symbolLotBufferArray() {
        return this.formData.symbolLotArray.map((curr) => ({
          key: curr,
          label: curr,
        }));
      },
      isLimitReached() {
        return this.searchCount > MAX_PRINTABLE_RESULTS;
      },
    },
    methods: {
      ...mapActions(['updateForm']),
      async searchCustomer({ text, cb, field, exact }) {
        if (text.length >= 3) {
          const response = await customersApi.autocompleteFetch(
            field,
            text,
            exact
          );
          const customers = response.data.map((c) => ({
            value: c.id,
            label: c[field],
            code: `${c.id}`,
            customerName: c.name,
          }));
          cb(customers);
        } else {
          cb([]);
        }
      },
      selectCustomer(item) {
        this.formData.customerId = item.value;
        this.formData.customerName = item.customerName;
        this.formData.code = item.code;
        this.updateStoredForm();
      },
      emptySelectedCustomer(text) {
        if (text === '') {
          this.formData.customerId = null;
          this.formData.customerName = '';
          this.formData.code = '';
          this.updateStoredForm();
        }
      },
      updateStoredForm() {
        // Clears search results on form change
        this.searchDone = false;
        this.searchCount = 0;
        this.searchMessage = null;
        this.updateForm({ formKey: 'analysesSearch', formData: this.formData });
      },
      addedSymbol(newValue) {
        this.symbolBuffer = '';
        if (
          this.formData.symbolArray.filter((current) => current === newValue)
            .length === 0
        ) {
          this.formData.symbolArray.push(newValue);
        }
      },
      removedSymbol({ item }) {
        const index = this.formData.symbolArray.indexOf(item.key);
        if (index > -1) {
          this.formData.symbolArray.splice(index, 1);
        }
      },
      addedSymbolLot(newValue) {
        this.symbolLotBuffer = '';
        if (
          this.formData.symbolLotArray.filter(
            (current) => current === `${newValue}`
          ).length === 0
        ) {
          this.formData.symbolLotArray.push(`${newValue}`);
        }
      },
      removedSymbolLot({ item }) {
        const index = this.formData.symbolLotArray.indexOf(item.key);
        if (index > -1) {
          this.formData.symbolLotArray.splice(index, 1);
        }
      },
      buildSearchFilters() {
        const {
          sampleReceivedFrom,
          sampleReceivedTo,
          sampleCompletedFrom,
          sampleCompletedTo,
          printType,
          customerName,
          code,
          productName,
          value,
          symbolArray,
          symbolLotArray,
          ...formData
        } = this.formData;

        const filters = Object.keys(formData).reduce((acc, key) => {
          if (formData[key]) return { ...acc, [key]: formData[key] };
          return acc;
        }, {});

        if (sampleReceivedFrom) {
          filters.analysisStart = {
            ...filters.analysisStart,
            gt: sampleReceivedFrom,
          };
        }
        if (sampleReceivedTo) {
          filters.analysisStart = {
            ...filters.analysisStart,
            lt: sampleReceivedTo,
          };
        }
        if (sampleCompletedFrom) {
          filters.analysisEnd = {
            ...filters.analysisEnd,
            gt: sampleCompletedFrom,
          };
        }
        if (sampleCompletedTo) {
          filters.analysisEnd = {
            ...filters.analysisEnd,
            lt: sampleCompletedTo,
          };
        }
        if (productName) {
          filters.productName = { regexp: `/${productName}/i` };
        }
        if (symbolArray.length > 0) {
          filters.symbol = { inq: symbolArray };
        }
        if (symbolLotArray.length > 0) {
          filters.symbolLot = { inq: symbolLotArray };
        }
        if (value) {
          filters.value = { regexp: `/${value}/i` };
        }

        return filters;
      },
      buildPrintFilters() {
        const { printType, ...formData } = this.formData;
        return Object.keys(formData).map((key) => ({
          key,
          label: this.$t(`analysesSearch.${key}`),
          value: formData[key],
        }));
      },
      async countSearchResults() {
        // Loading spinner
        const loading = this.$loading({
          lock: true,
          background: 'rgba(48,53,82,0.9)',
        });
        try {
          const filters = this.buildSearchFilters();
          const response = await samplesApi.sampleAnalysesSearchCount(filters);

          const { count } = response.data;
          this.searchDone = true;
          this.searchCount = count;
          this.searchMessage = this.$tc('analysesSearch.searchResult', count);

          loading.close();
        } catch (error) {
          this.searchDone = false;
          this.searchCount = 0;
          this.searchMessage = null;
          loading.close();
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async printSearchResults() {
        // Loading spinner
        const loading = this.$loading({
          lock: true,
          background: 'rgba(48,53,82,0.9)',
        });
        try {
          const filters = this.buildSearchFilters();
          const printFilters = this.buildPrintFilters();
          const response = await samplesApi.sampleAnalysesSearch({
            filters,
            printType: this.formData.printType,
            printFilters,
          });

          loading.close();
          printJS(URL.createObjectURL(response.data));
          // fileSaver.saveAs(response.data, `samples-search-results-${this.formData.printType}.pdf`);
        } catch (error) {
          loading.close();
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async exportResultsExcel() {
        // Loading spinner
        const loading = this.$loading({
          lock: true,
          background: 'rgba(48,53,82,0.9)',
        });
        try {
          const filters = this.buildSearchFilters();
          const printFilters = this.buildPrintFilters();
          const response = await samplesApi.sampleAnalysesSearchExport({
            filters,
            printType: this.formData.printType,
            printFilters,
          });

          loading.close();
          fileSaver.saveAs(
            response.data,
            `samples-search-results-${this.formData.printType}.xlsx`
          );
        } catch (error) {
          loading.close();
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
    },
    created() {
      this.formData = Object.keys(this.forms.analysesSearch).length
        ? { ...this.forms.analysesSearch }
        : { ...initialFormData };
    },
  };
</script>

<style lang="scss">
  .analyses-search-form {
    &__results {
      color: $--color-white;
      display: block;
      min-height: 32px;
    }
  }
</style>
